import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { Row, Col, Select, InputNumber, Button, Tooltip, Space } from '@app/ui';
import { capitalize } from '@app/utils/capitalize';
import { isAfter, isBefore, isInBetween, numberOptions } from './options';
import usePrevious from '@app/components/hooks/usePrevious';

const { Option } = Select;

const NumberPicker = ({
    param,
    setSelectedParam,
    allSelectedParams,
    type = 'number',
    disabled,
    min = 0,
    max,
}) => {
    const { t } = useTranslation(['tools', 'common']);
    const selected = useMemo(
        () => allSelectedParams[param],
        [param, allSelectedParams],
    );

    const prevParam = usePrevious(param);
    const isPerc = type == 'perc';

    const startValue = selected && selected.start;
    const endValue = selected && selected.end;
    const [subs, setSubs] = useState([]);
    const [option, setOption] = useState(null);

    // allow one decimal point for percentage
    const [startVal, setStartVal] = useState(
        isPerc ? Math.floor(startValue * 1000) / 10 : startValue,
    );
    const [endVal, setEndVal] = useState(
        isPerc ? Math.floor(endValue * 1000) / 10 : endValue,
    );

    const showStart = R.includes('start', subs);
    const showEnd = R.includes('end', subs);

    useEffect(() => {
        if (disabled) return;
        if (prevParam) {
            setOption(undefined);
            setSubs([]);
        } else {
            // Ensure selected is an object before calling Object.keys
            const safeSelected =
                selected && typeof selected === 'object' ? selected : {};
            const newSubs = Object.keys(safeSelected);
            let newOpt;
            if (newSubs.length > 1) {
                newOpt = isInBetween;
            } else if (newSubs[0] == 'end') {
                newOpt = isBefore;
            } else {
                newOpt = isAfter;
            }
            setOption(newOpt);
            setSubs(newSubs);
        }
    }, [param]);

    useEffect(() => {
        if (!disabled) return;
        // Ensure selected is an object before calling Object.keys
        const safeSelected =
            selected && typeof selected === 'object' ? selected : {};
        if (Object.keys(safeSelected).length > 0) {
            const newSubs = Object.keys(safeSelected);
            let newOpt;
            if (newSubs.length > 1) {
                newOpt = isInBetween;
            } else if (newSubs[0] == 'end') {
                newOpt = isBefore;
            } else {
                newOpt = isAfter;
            }
            setOption(newOpt);
        }
    }, [selected]);

    const opt = useMemo(() => {
        return numberOptions.find(d => d.value == option);
    }, [option]);

    useEffect(() => {
        if (!opt) return;
        // Resetting the subs if they are not present
        // so that is does not stick with "in between"
        setSubs(() => {

            if (!opt.subs.includes('start')) setStartVal(undefined);
            if (!opt.subs.includes('end')) setEndVal(undefined);

            return opt.subs;
        });
    }, [opt]);

    const _selectValue = sub => v => {
        let val = undefined;

        if (typeof v === 'number') val = isPerc ? v / 100 : v;

        setSelectedParam({
            param,
            sub,
        })(val);
    };

    useEffect(() => {
        _selectValue('start')(startVal);
    }, [startVal]);

    useEffect(() => {
        _selectValue('end')(endVal);
    }, [endVal]);

    const _onChange = sub => value => {
        if (!sub) return;
        if (sub == 'start') {
            setStartVal(value);
        }
        if (sub == 'end') {
            setEndVal(value);
        }
    };

    const showTooltip = isPerc && !disabled;

    const getOptionLabel = optValue => {
        switch (optValue) {
            case isAfter:
                return t('tools:automatedParams.options.comparison.gte');
            case isBefore:
                return t('tools:automatedParams.options.comparison.lte');
            case isInBetween:
                return t('tools:automatedParams.options.comparison.inBetween');
            default:
                return '';
        }
    };

    return (
        <Row gutter={16} align="middle">
            <Col span={8}>
                {!disabled && (
                    <Select
                        style={{ width: '100%' }}
                        placeholder={t(
                            'tools:automatedParams.placeholders.filter',
                        )}
                        value={option}
                        onChange={setOption}
                        disabled={disabled}
                    >
                        {numberOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                                {getOptionLabel(option.value)}
                            </Option>
                        ))}
                    </Select>
                )}
                {disabled && !!opt && (
                    <Button
                        type="primary"
                        style={{ width: '100%' }}
                        disabled={disabled}
                    >
                        {`${capitalize(getOptionLabel(opt.value))}`}
                    </Button>
                )}
            </Col>

            <Col span={6}>
                {showStart && (
                    <Space>
                        <InputNumber
                            style={{ width: '100%' }}
                            value={startVal}
                            onChange={_onChange('start')}
                            disabled={disabled}
                            min={min}
                            max={endVal || max}
                            step={isPerc ? 0.1 : 1}
                        />
                        {isPerc && <span>%</span>}
                    </Space>
                )}

                {showEnd && !showStart && (
                    <Space>
                        <InputNumber
                            style={{ width: '100%' }}
                            value={endVal}
                            onChange={_onChange('end')}
                            disabled={disabled}
                            min={startVal || min}
                            max={max}
                            step={isPerc ? 0.1 : 1}
                        />
                        {isPerc && <span>%</span>}
                    </Space>
                )}
            </Col>

            <Col
                span={2}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {showEnd && showStart && (
                    <span>{t('tools:automatedParams.options.comparison.and')}</span>
                )}
            </Col>

            <Col span={6}>
                {showEnd && showStart && (
                    <Space>
                        <InputNumber
                            style={{ width: '100%' }}
                            value={endVal}
                            onChange={_onChange('end')}
                            disabled={disabled}
                            min={startVal || min}
                            max={max}
                            step={isPerc ? 0.1 : 1}
                        />
                        {isPerc && <span>%</span>}
                    </Space>
                )}
            </Col>

            <Col span={2}>
                {showTooltip && (
                    <Tooltip title={t('tools:automatedParams.tooltips.mvpTooltip')}>
                        <i className="fas fa-info-circle" />
                    </Tooltip>
                )}
            </Col>
        </Row>
    );
};

NumberPicker.propTypes = {
    param: PropTypes.string,
    setSelectedParam: PropTypes.func.isRequired,
    allSelectedParams: PropTypes.object.isRequired,
    type: PropTypes.string,
    disabled: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
};

export default NumberPicker;
